import { CSSProperties, ReactNode, Suspense, useEffect, useState } from 'react';
import { BOX_SHADOWS, MEDIA_QUERIES } from 'shared/styles/variables';
import styled, { DefaultTheme, ThemeProvider } from 'styled-components';

import FocusTrap from 'inquiry/components/FocusTrap';
import GlobalStyles from 'verify/components/GlobalStyles';
import LocalizedDirection from 'verify/components/LocalizedDirection';
import SandboxBanner from 'verify/components/SandboxBanner';
import ThemedFonts from 'verify/components/ThemedFonts';
import { HolderClient } from 'verify/lib/clients/HolderClient';
import FlowType from 'verify/lib/interfaces/FlowType';
import { PAGE_WIDTHS } from 'verify/styles/variables';

export const FRAME_MARGIN_Y = 50;
export const FRAME_HEIGHT = 650;

const ContainerWrapper = styled('div')<{ flowType: FlowType; extraMarginTop: boolean }>`
  position: relative;
  height: 100%;
  width: 100%;

  ${({ flowType, extraMarginTop }) => {
    if (flowType === FlowType.Inline) {
      return null;
    }

    // TODO (lianga): see if we can just do 70px margin-top uniformly instead of conditionally
    return extraMarginTop
      ? `
        @media ${MEDIA_QUERIES.isNotMobile} {
          margin-top: 70px;
          margin-bottom: 50px;
        }
      `
      : `
        @media ${MEDIA_QUERIES.isNotMobile} {
          margin-top: 50px;
          margin-bottom: 50px;
        }
      `;
  }}
`;

const Container = styled('div')<{ flowType: FlowType; hidden: boolean }>`
  ${({ hidden }) => hidden && `display: none;`};
  position: relative;
  color: ${({ theme }) => theme.colorFont};

  @media ${MEDIA_QUERIES.isMobile} {
    // 100vh height is a known problem for mobile browsers as the real height is dynamic due to
    // components like the browser's nav bar, menu bar, etc. that appear and disappear dynamically.
    // This is a default and we try to compute the real height of viewport and overwrite with inline
    // style.
    height: 100vh;
  }

  @media ${MEDIA_QUERIES.isNotMobile} {
    border-radius: ${({ theme }) => theme.borderRadiusModal};
    margin-left: auto;
    margin-right: auto;
    ${({ flowType }) =>
      flowType === FlowType.Inline
        ? null
        : `
          max-width: ${PAGE_WIDTHS.small};
          box-shadow: 0 5px 15px 0 ${BOX_SHADOWS.base};
        `}
    min-height: ${FRAME_HEIGHT}px;
    max-height: 100%;
    /* Cut off things like dialogs, notifications, etc. that animate in from off screen. */
    overflow-y: hidden;
  }
`;

export const FRAME_ID = 'application__frame';

interface FrameProps {
  isSandbox?: boolean;
  sandboxBannerDisabled?: boolean;
  children: ReactNode;
  footerWidgets?: ReactNode;
  themeSerialized: DefaultTheme;
  flowType: FlowType;
  hidden: boolean;
  'data-test': string;
}

export default function Frame(props: FrameProps) {
  const height = use100vh();

  const containerInlineStyle: CSSProperties = {};
  if (window.matchMedia(MEDIA_QUERIES.isMobile).matches) {
    containerInlineStyle.height = height ? `${height}px` : '100vh';
  }

  return (
    <FocusTrap
      // Use disabled instead of active because this is not expected to change at runtime.
      disabled={!HolderClient.isEmbedded}
      focusTrapOptions={{
        // Block ALL outside interaction.
        escapeDeactivates: false,
        clickOutsideDeactivates: false,
        allowOutsideClick: false,
        // We coordinate focus outside with refs, which is easier when animations are involved.
        initialFocus: false,
        // Needed to stop FocusTrap from erroring before content has rendered.
        fallbackFocus: document.body,
        setReturnFocus: false,
      }}
    >
      <div id={FRAME_ID} data-test={props['data-test']}>
        <ThemeProvider theme={props.themeSerialized}>
          <GlobalStyles flowType={props.flowType} />
          <Suspense fallback={null}>
            <LocalizedDirection>
              <ThemedFonts>
                {props.isSandbox && !props.sandboxBannerDisabled && <SandboxBanner />}
                <ContainerWrapper
                  flowType={props.flowType}
                  // FIXME (lianga): This is a temporary hack to provide space for the
                  //  FLE preview banner, since we only disable the sandbox banner in
                  //  this context
                  extraMarginTop={!!(props.isSandbox && props.sandboxBannerDisabled)}
                >
                  <Container
                    data-test='frame__container'
                    flowType={props.flowType}
                    style={containerInlineStyle}
                    hidden={props.hidden}
                  >
                    {props.children}
                  </Container>
                  {props.footerWidgets}
                </ContainerWrapper>
              </ThemedFonts>
            </LocalizedDirection>
          </Suspense>
        </ThemeProvider>
      </div>
    </FocusTrap>
  );
}

// Copied over from https://github.com/mvasin/react-div-100vh. See note above about 100vh on mobile.
function use100vh(): number | null {
  const [height, setHeight] = useState<number | null>(measureHeight);

  const wasRenderedOnClientAtLeastOnce = useWasRenderedOnClientAtLeastOnce();

  useEffect(() => {
    if (!wasRenderedOnClientAtLeastOnce) return;

    function setMeasuredHeight() {
      const measuredHeight = measureHeight();
      setHeight(measuredHeight);
    }

    window.addEventListener('resize', setMeasuredHeight);
    return () => window.removeEventListener('resize', setMeasuredHeight);
  }, [wasRenderedOnClientAtLeastOnce]);
  return wasRenderedOnClientAtLeastOnce ? height : null;
}

export function measureHeight(): number | null {
  if (!isClient()) return null;
  return document.documentElement?.clientHeight || window.innerHeight;
}

// Once we ended up on the client, the first render must look the same as on
// the server so hydration happens without problems. _Then_ we immediately
// schedule a subsequent update and return the height measured on the client.
// It's not needed for CSR-only apps, but is critical for SSR.
function useWasRenderedOnClientAtLeastOnce() {
  const [wasRenderedOnClientAtLeastOnce, setWasRenderedOnClientAtLeastOnce] = useState(false);

  useEffect(() => {
    if (isClient()) {
      setWasRenderedOnClientAtLeastOnce(true);
    }
  }, []);
  return wasRenderedOnClientAtLeastOnce;
}

function isClient() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}
